import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import {
    colors, radii, fontSizes, fontWeights, shadows, space
} from 'style/config';

const HASH_PARAM = 'activeTab';
const KEYBOARD_KEYS = {
    ARROW_RIGHT: 'ArrowRight',
    ARROW_LEFT: 'ArrowLeft',
    HOME: 'Home',
    END: 'End'
};

const getTabKeys = (labelArray, keyArray) => (keyArray?.length === labelArray.length ? keyArray : labelArray);
const getTabKeySignature = (labelArray, keyArray) => getTabKeys(labelArray, keyArray).join('|');

const getHashKey = tabKeyArray => {
    if (typeof window === 'undefined') {
        return null;
    }

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const fromUrl = hashParams.get(HASH_PARAM);

    return tabKeyArray.includes(fromUrl) ? fromUrl : null;
};

const updateHash = key => {
    if (typeof window === 'undefined') {
        return;
    }

    const hashParams = new URLSearchParams(window.location.hash.slice(1));

    hashParams.set(HASH_PARAM, key);
    window.history.replaceState({}, null, `${window.location.pathname}${window.location.search}#${hashParams.toString()}`);
};

const getNextTabIndex = (currentIndex, key, tabCount) => {
    if (key === KEYBOARD_KEYS.ARROW_RIGHT) {
        return (currentIndex + 1) % tabCount;
    }

    if (key === KEYBOARD_KEYS.ARROW_LEFT) {
        return (currentIndex - 1 + tabCount) % tabCount;
    }

    if (key === KEYBOARD_KEYS.HOME) {
        return 0;
    }

    if (key === KEYBOARD_KEYS.END) {
        return tabCount - 1;
    }

    return null;
};

/**
 * Tab rail component with hash-synced selection.
 *
 * @param {Object} props
 * @param {string[]} props.labelArray Tab labels in display order.
 * @param {React.ReactNode[]} props.contentArray Tab panel content in the same order as labels.
 * @param {string[] | null} [props.keyArray=null] Optional stable keys for each tab.
 * @param {Object|Object[]|null} [props.tabLabelCss=null] Optional Emotion styles for each tab label.
 * @param {(tabKey: string) => void} [props.onTabChange] Callback invoked only for user-initiated tab changes (click/keyboard).
 */
const Tab = ({
    labelArray, contentArray, keyArray = null, tabLabelCss = null, onTabChange
}) => {
    const tabKeySignature = getTabKeySignature(labelArray, keyArray);
    const tabKeyArray = useMemo(() => tabKeySignature.split('|'), [tabKeySignature]);
    const [activeKey, setActiveKey] = useState(tabKeyArray[0]);

    useEffect(() => {
        const syncHash = () => {
            const hashKey = getHashKey(tabKeyArray);

            if (hashKey) {
                setActiveKey(hashKey);

                if (typeof onTabChange === 'function') {
                    onTabChange(hashKey);
                }

                return;
            }

            setActiveKey(tabKeyArray[0]);
            updateHash(tabKeyArray[0]);
        };

        syncHash();
        window.addEventListener('hashchange', syncHash);

        return () => window.removeEventListener('hashchange', syncHash);
    }, [tabKeyArray]);

    const handleTabChange = tabKey => {
        setActiveKey(tabKey);
        updateHash(tabKey);

        if (typeof onTabChange === 'function') {
            onTabChange(tabKey);
        }
    };

    const handleTabClick = event => {
        handleTabChange(event.currentTarget.dataset.tabKey);
    };

    const activeIndex = Math.max(tabKeyArray.indexOf(activeKey), 0);

    const handleTabKeyDown = event => {
        const nextIndex = getNextTabIndex(activeIndex, event.key, tabKeyArray.length);

        if (nextIndex === null) {
            return;
        }

        event.preventDefault();
        const nextKey = tabKeyArray[nextIndex];

        handleTabChange(nextKey);
        event.currentTarget.parentElement?.querySelector(`[data-tab-key="${nextKey}"]`)?.focus();
    };

    return (
        <Box>
            <Box
                css={styles.container}
                data-at='bi_tab_rail'
                role='tablist'
            >
                {labelArray.map((label, index) => {
                    const tabKey = tabKeyArray[index];
                    const isActive = tabKey === activeKey;

                    return (
                        <Box
                            is='button'
                            key={tabKey}
                            type='button'
                            css={[styles.tab, isActive && styles.activeTab]}
                            data-at={`bi_tab_${tabKey}`}
                            data-tab-key={tabKey}
                            id={`bi-tab-${tabKey}`}
                            onClick={handleTabClick}
                            onKeyDown={handleTabKeyDown}
                            role='tab'
                            aria-selected={isActive}
                            aria-controls={`bi-tab-panel-${tabKey}`}
                            tabIndex={isActive ? 0 : -1}
                        >
                            <Text
                                is='span'
                                lineHeight='tight'
                                css={[styles.tabLabel, tabLabelCss]}
                            >
                                {label}
                            </Text>
                        </Box>
                    );
                })}
            </Box>
            <Box
                id={`bi-tab-panel-${tabKeyArray[activeIndex]}`}
                role='tabpanel'
                aria-labelledby={`bi-tab-${tabKeyArray[activeIndex]}`}
                css={styles.content}
            >
                {contentArray[activeIndex]}
            </Box>
        </Box>
    );
};

const styles = {
    container: {
        display: 'flex',
        width: '100%',
        background: colors.black,
        borderRadius: radii.full,
        height: '40px',
        padding: space[1],
        alignItems: 'stretch'
    },
    tab: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radii.full,
        padding: `0 ${space[2]}px`,
        cursor: 'pointer',
        color: colors.midGray,
        background: 'transparent',
        border: 0,
        appearance: 'none',
        font: 'inherit',
        transition: 'background 0.2s, color 0.2s',
        '.no-touch &:hover': {
            color: colors.white
        }
    },
    activeTab: {
        background: colors.white,
        color: colors.black,
        boxShadow: shadows.light,
        '.no-touch &:hover': {
            color: colors.black
        }
    },
    tabLabel: {
        fontSize: fontSizes.sm,
        fontWeight: fontWeights.bold,
        whiteSpace: 'nowrap'
    }
};

Tab.propTypes = {
    labelArray: PropTypes.arrayOf(PropTypes.string).isRequired,
    contentArray: PropTypes.arrayOf(PropTypes.node).isRequired,
    keyArray: PropTypes.arrayOf(PropTypes.string),
    tabLabelCss: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
};

export default wrapFunctionalComponent(Tab, 'Tab');
